import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { FileText, Calendar, User as UserIcon, Download, Search, Activity, TrendingUp, ClipboardList, Send, Clock, CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '../../config/supabase';
import { toast } from 'react-toastify';
import { getPatientMedicalRecordsAcrossClients } from '../../services/MedicalRecordService';
import LinkedPatientBadge from '../../components/admin/LinkedPatientBadge';
import PrivacyConsentModal from '../../components/patient/PrivacyConsentModal';
import AssessmentDetailModal from '../../components/patient/AssessmentDetailModal';
import { getMedicalRecordDownloadUrl } from '../../utils/medicalRecordFiles';

const EVOLUTION_REQUESTS_ENABLED = (import.meta.env.VITE_FEATURE_EVOLUTION_REQUESTS ?? 'false') === 'true';

interface MedicalRecord {
  id: string;
  name: string;
  url: string;
  uploadDate: Date;
  patientName: string;
  doctorName: string;
  patientId: string;
  doctorId: string;
  clientId?: string;
  clientName?: string;
  metadata: any;
}

interface Assessment {
  id: string;
  patientId: string;
  patientName: string;
  doctorId?: string;
  doctorName?: string;
  clientId?: string;
  clientName?: string;
  type: string;
  tests?: string[];
  datetime: string;
  notes?: string;
  createdAt: any;
  data?: Record<string, any>;
}

interface EvolutionRequest {
  id: string;
  userId: string;
  patientRecordId: string;
  requestDate: any;
  status: 'pending' | 'approved' | 'rejected';
  processedBy?: string;
  processedAt?: any;
  notes?: string;
}

const PatientDashboard = () => {
  const { user, selectedClient, availableClients } = useAuth();
  const clientId = selectedClient?.clientId;
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [userData, setUserData] = useState(user);
  const [patientRecord, setPatientRecord] = useState<any>(null);
  const [loadingPatientRecord, setLoadingPatientRecord] = useState(true);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [assessmentSortOrder, setAssessmentSortOrder] = useState<'asc' | 'desc'>('desc');
  const [evolutionRequests, setEvolutionRequests] = useState<EvolutionRequest[]>([]);
  const [requestNotes, setRequestNotes] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [evolutionRequestsAvailable, setEvolutionRequestsAvailable] = useState(EVOLUTION_REQUESTS_ENABLED);
  const [consentedClientIds, setConsentedClientIds] = useState<string[]>([]);
  const [pendingConsentClientIds, setPendingConsentClientIds] = useState<string[]>([]);

  const resolveClientName = (id: string) =>
    availableClients.find((c) => c.clientId === id)?.clientName || id;

  const loadConsentState = async (patientData: any) => {
    const patientId = patientData?.patientId;
    const linkedClientIds = (patientData?.patient_clients || [])
      .map((item: any) => item?.clientId)
      .filter(Boolean);
    const availableClientIds = new Set((availableClients || []).map((client) => client.clientId));
    const eligibleClientIds = linkedClientIds.filter((id: string) => availableClientIds.has(id));

    if (!patientId || eligibleClientIds.length === 0) {
      setConsentedClientIds([]);
      setPendingConsentClientIds([]);
      setHasConsent(false);
      return;
    }

    const { data, error } = await supabase
      .from('patient_consents')
      .select('clientId, consentStatus')
      .eq('patientId', patientId)
      .eq('consentType', 'data_access')
      .in('clientId', eligibleClientIds);

    if (error) {
      console.error('Erro ao carregar consentimentos do paciente:', error);
      toast.error('Erro ao carregar consentimentos');
      return;
    }

    const grantedIds = (data || [])
      .filter((row: any) => row.consentStatus === 'granted')
      .map((row: any) => row.clientId);

    const pendingIds = eligibleClientIds.filter((id: string) => !grantedIds.includes(id));

    setConsentedClientIds(grantedIds);
    setPendingConsentClientIds(pendingIds);
    setHasConsent(grantedIds.length > 0);
  };

  useEffect(() => {
    if (!user?.userId || !clientId) return;

    const fetchUserData = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*, user_clients(userRole, clientId)') // Seleciona roles por tenant
        .eq('userId', user.userId)
        .single();

      if (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        toast.error('Erro ao atualizar dados do usuário');
        return;
      }

      if (data) {
        const scopedUserClient = data.user_clients?.find((item: any) => item.clientId === clientId);
        const userRole = scopedUserClient?.userRole || user.userRole;

        setUserData({
          ...user,
          ...data,
          userRole: userRole, // Atualiza o userRole para o role do Supabase
        });
      }
    };

    fetchUserData();

    // Para simular onSnapshot, você pode usar subscriptions do Supabase, mas para este caso, uma busca única é suficiente.
    // const channel = supabase
    //   .channel(`user_changes:${user.id}`)
    //   .on('postgres_changes',
    //     { event: 'UPDATE', schema: 'public', table: 'users', filter: `userId=eq.${user.id}` },
    //     (payload) => {
    //       console.log('Change received!', payload);
    //       fetchUserData(); // Re-fetch data on update
    //     }
    //   )
    //   .subscribe();

    // return () => {
    //   supabase.removeChannel(channel);
    // };
  }, [user?.userId, clientId]);

  useEffect(() => {
    const loadRecords = async () => {
      if (!patientRecord?.patientId) return;

      try {
        setIsLoading(true);
        if (consentedClientIds.length === 0) {
          setRecords([]);
          return;
        }
        const fetchedRecords = await getPatientMedicalRecordsAcrossClients(
          patientRecord.patientId,
          consentedClientIds
        );
        setRecords(fetchedRecords);
      } catch (error) {
        console.error('Error loading records:', error);
        toast.error('Erro ao carregar exames e documentos');
      } finally {
        setIsLoading(false);
      }
    };

    loadRecords();
  }, [patientRecord?.patientId, consentedClientIds]);

  useEffect(() => {
    const loadPatientRecord = async () => {
      if (!user?.userId) return; // Usar user.id do Supabase

      try {
        setLoadingPatientRecord(true);
        const { data, error } = await supabase
          .from('patients')
          .select(`
            *,
            patient_clients(clientId)
          `)
          .eq('userId', user.userId)
          .maybeSingle();

        if (error) {
          console.error('Erro ao carregar cadastro de paciente:', error);
          return;
        }

        const record = data || null;
        setPatientRecord(record);

        if (!record) {
          console.log('Usuário não possui cadastro de paciente vinculado');
          setConsentedClientIds([]);
          setPendingConsentClientIds([]);
          setHasConsent(false);
          return;
        }

        await loadConsentState(record);
      } catch (error) {
        console.error('Erro ao carregar cadastro de paciente:', error);
      } finally {
        setLoadingPatientRecord(false);
      }
    };

    loadPatientRecord();
  }, [user?.userId, availableClients]);

  useEffect(() => {
    if (!patientRecord?.patientId) return;

    const loadAssessments = async () => {
      if (consentedClientIds.length === 0) {
        setAssessments([]);
        setLoadingAssessments(false);
        return;
      }

      setLoadingAssessments(true);
      try {
        const { data, error } = await supabase
          .from('medical_records')
          .select(`
            recordId,
            patientId,
            patients(patientFullName),
            doctorId,
            users(userDisplayName),
            clientId,
            clients(clientName),
            recordType,
            recordDate,
            recordTitle,
            recordDescription,
            recordAttachments,
            recordCreatedAt
          `)
          .eq('patientId', patientRecord.patientId)
          .in('clientId', consentedClientIds)
          .eq('recordType', 'evaluation')
          .order('recordCreatedAt', { ascending: false });

        if (error) {
          console.error('Erro ao carregar avaliações:', error);
          toast.error('Erro ao carregar avaliações');
          return;
        }

        setAssessments((data || []).map((assessment: any) => {
          const patientName = Array.isArray(assessment.patients)
            ? assessment.patients[0]?.patientFullName
            : assessment.patients?.patientFullName;
          const doctorName = Array.isArray(assessment.users)
            ? assessment.users[0]?.userDisplayName
            : assessment.users?.userDisplayName;
          const clientName = Array.isArray(assessment.clients)
            ? assessment.clients[0]?.clientName
            : assessment.clients?.clientName;

          return {
            id: assessment.recordId,
            patientId: assessment.patientId,
            patientName: patientName || patientRecord?.patientFullName || userData?.userDisplayName || 'Paciente',
            doctorId: assessment.doctorId,
            doctorName: doctorName || 'Profissional não informado',
            clientId: assessment.clientId,
            clientName: clientName || assessment.clientId,
            type: assessment.recordTitle || assessment.recordType,
            tests: assessment.recordAttachments?.tests || [],
            datetime: assessment.recordDate,
            notes: assessment.recordDescription,
            createdAt: assessment.recordCreatedAt,
            data: assessment.recordAttachments?.data || assessment.recordAttachments,
          } as Assessment;
        }));
      } finally {
        setLoadingAssessments(false);
      }
    };

    loadAssessments();
  }, [patientRecord?.patientId, patientRecord?.patientFullName, consentedClientIds, userData?.userDisplayName]);

  useEffect(() => {
    if (
      activeTab !== 'request-evolutions' ||
      !user?.userId ||
      !patientRecord?.patientId ||
      !clientId ||
      !evolutionRequestsAvailable ||
      !consentedClientIds.includes(clientId)
    ) return;

    const loadEvolutionRequests = async () => {
      const { data, error } = await supabase
        .from('evolution_requests')
        .select('*')
        .eq('userId', user.userId) // Usar user.id do Supabase
        .eq('patientId', patientRecord.patientId)
        .eq('clientId', clientId)
        .order('requestCreatedAt', { ascending: false }); // Usar requestCreatedAt para ordenação

      if (error) {
        if ((error as any).code === 'PGRST205') {
          setEvolutionRequestsAvailable(false);
          setEvolutionRequests([]);
          return;
        }
        console.error('Erro ao carregar solicitações:', error);
        toast.error('Erro ao carregar solicitações');
        return;
      }

      setEvolutionRequests((data || []).map(request => ({
        id: request.requestId, // Assumindo requestId como o ID
        userId: request.userId,
        patientRecordId: request.patientId, // Usar patientId
        requestDate: request.requestCreatedAt, // Usando requestCreatedAt para requestDate
        status: request.requestStatus, // Assumindo requestStatus
        processedBy: request.processedBy,
        processedAt: request.processedAt,
        notes: request.requestNotes,
      } as EvolutionRequest)));
    };

    loadEvolutionRequests();
  }, [activeTab, user?.userId, patientRecord?.patientId, clientId, evolutionRequestsAvailable, consentedClientIds]);

  const filteredRecords = records.filter(record => 
    record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.doctorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDownload = async (record: MedicalRecord) => {
    try {
      const url = await getMedicalRecordDownloadUrl(record.metadata);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Falha ao baixar arquivo (${response.status})`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Arquivo retornado está vazio.');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = record.name || 'documento';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erro ao baixar documento, abrindo em nova aba:', error);
      try {
        const fallbackUrl = await getMedicalRecordDownloadUrl(record.metadata);
        window.open(fallbackUrl, '_blank');
      } catch (fallbackError) {
        console.error('Erro ao abrir documento em nova aba:', fallbackError);
        toast.info('Este registro não possui arquivo para download.');
      }
    }
  };

  const handleConsentAccept = async () => {
    if (!user?.userId || !selectedClient?.clientId || !patientRecord?.patientId) {
      toast.error('Não foi possível registrar o consentimento para este tenant');
      return;
    }

    try {
      const targetClientId = selectedClient.clientId;
      const now = new Date().toISOString();
      const consentVersion = 'v1.0';
      const linkedClientIds = (patientRecord.patient_clients || [])
        .map((item: any) => item?.clientId)
        .filter(Boolean);

      if (!linkedClientIds.includes(targetClientId)) {
        toast.error('Este tenant não está vinculado ao cadastro do paciente.');
        return;
      }

      const { data: existingConsent, error: existingError } = await supabase
        .from('patient_consents')
        .select('patientConsentId, consentStatus')
        .eq('patientId', patientRecord.patientId)
        .eq('clientId', targetClientId)
        .eq('consentType', 'data_access')
        .maybeSingle();

      if (existingError && (existingError as any).code !== 'PGRST116') {
        console.error('Erro ao consultar consentimento existente:', existingError);
        toast.error('Erro ao registrar consentimento');
        return;
      }

      const { data: upsertedConsent, error: upsertError } = await supabase
        .from('patient_consents')
        .upsert(
          {
            patientId: patientRecord.patientId,
            clientId: targetClientId,
            consentType: 'data_access',
            consentStatus: 'granted',
            consentVersion,
            consentGrantedAt: now,
            consentRevokedAt: null,
            consentCapturedByUserId: user.userId,
            consentCaptureSource: 'patient_portal',
            consentUpdatedAt: now,
          },
          { onConflict: 'patientId,clientId,consentType' }
        )
        .select('patientConsentId')
        .single();

      if (upsertError) {
        console.error('Erro ao registrar consentimento atual:', upsertError);
        toast.error('Erro ao registrar consentimento');
        return;
      }

      const { error: eventError } = await supabase
        .from('patient_consent_events')
        .insert({
          patientConsentId: upsertedConsent?.patientConsentId || existingConsent?.patientConsentId || null,
          clientId: targetClientId,
          patientId: patientRecord.patientId,
          consentType: 'data_access',
          eventAction: 'granted',
          previousStatus: existingConsent?.consentStatus || null,
          newStatus: 'granted',
          consentVersion,
          actorUserId: user.userId,
          actorDisplayName: userData?.userDisplayName || null,
          actorEmail: userData?.userEmail || null,
          snapshot: {
            source: 'patient_dashboard',
            acceptedAt: now,
          },
        });

      if (eventError) {
        console.error('Erro ao registrar evento de consentimento:', eventError);
      }

      await loadConsentState(patientRecord);
      setShowConsentModal(false);
      setActiveTab('assessments');
      toast.success('Consentimento registrado com sucesso!');
    } catch (error) {
      console.error('Erro ao registrar consentimento:', error);
      toast.error('Erro ao registrar consentimento');
    }
  };

  const handleConsentDecline = () => {
    setShowConsentModal(false);
    setActiveTab('overview');
    toast.info('Voc\u00ea pode acessar seus dados a qualquer momento aceitando o termo de consentimento');
  };

  const handleRequestEvolution = async () => {
    if (!user?.userId || !patientRecord?.patientId || !clientId) { // Usar user.id do Supabase
      toast.error('Dados do usuário não encontrados');
      return;
    }

    if (!evolutionRequestsAvailable) {
      toast.info('Solicitações de evolução estão temporariamente indisponíveis.');
      return;
    }

    if (!clientId || !consentedClientIds.includes(clientId)) {
      toast.error('É necessário consentimento ativo no tenant atual para enviar solicitações.');
      return;
    }

    if (!requestNotes.trim()) {
      toast.error('Por favor, descreva sua solicitação');
      return;
    }

    try {
      setIsSubmittingRequest(true);
      const { error } = await supabase
        .from('evolution_requests')
        .insert({
          clientId,
          userId: user.userId, // Usar user.id do Supabase
          patientId: patientRecord.patientId, // Certifique-se que o schema do Supabase usa 'patientId'
          requestCreatedAt: new Date().toISOString(), // Supabase usa timestamp no formato ISO
          requestStatus: 'pending', // Mapeando para requestStatus
          requestNotes: requestNotes.trim(), // Mapeando para requestNotes
          patientName: userData?.userDisplayName || '',
          patientEmail: userData?.userEmail || ''
        });

      if (error) {
        if ((error as any).code === 'PGRST205') {
          setEvolutionRequestsAvailable(false);
          toast.info('As solicitações de evolução estão indisponíveis no momento. Tente novamente mais tarde ou fale com a clínica.');
          setIsSubmittingRequest(false);
          return;
        }
        console.error('Erro ao enviar solicitação:', error);
        toast.error('Erro ao enviar solicitação');
        setIsSubmittingRequest(false);
        return;
      }

      setRequestNotes('');
      toast.success('Solicitação enviada com sucesso! Em breve processaremos seu pedido.');
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast.error('Erro ao enviar solicitação');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const getTestTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'CAT': 'Question\u00e1rio CAT',
      'mMRC': 'Escala mMRC',
      'PFSDQ-M': 'PFSDQ-M',
      'TC6': 'Teste de Caminhada 6min',
      '6MST': 'Teste do Degrau 6min',
      'CHESTER': 'Chester Step Test',
      '5xSTS': 'Sentar-Levantar 5x',
      'HADS': 'HADS',
      'PIMAX_SINDEX': 'PImax e S-Index',
      'SGRQ': 'Saint George',
      'LCADL': 'LCADL'
    };
    return labels[type] || type;
  };

  const toggleAssessmentSort = () => {
    setAssessmentSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const sortedAssessments = [...assessments].sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(a.datetime);
    const dateB = b.createdAt?.toDate?.() || new Date(b.datetime);
    return assessmentSortOrder === 'desc' 
      ? dateB.getTime() - dateA.getTime()
      : dateA.getTime() - dateB.getTime();
  });

  const hasConsentInSelectedClient = !!clientId && consentedClientIds.includes(clientId);

  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const calculateIMC = (weight: number, height: number): number => {
    if (!weight || !height || height === 0) return 0;
    const heightInMeters = height / 100;
    return weight / (heightInMeters * heightInMeters);
  };

  const getIMCClassification = (imc: number): string => {
    if (imc < 18.5) return 'Abaixo do peso';
    if (imc < 25) return 'Peso normal';
    if (imc < 30) return 'Sobrepeso';
    if (imc < 35) return 'Obesidade grau I';
    if (imc < 40) return 'Obesidade grau II';
    return 'Obesidade grau III';
  };

  const clinicalFullName =
    patientRecord?.patientFullName
    || `${patientRecord?.nome || ''} ${patientRecord?.sobrenome || ''}`.trim()
    || '-';
  const clinicalRecordNumber = patientRecord?.patientCpf || patientRecord?.prontu_x00c1_rio || '-';
  const clinicalBirthDate = patientRecord?.patientDateOfBirth || patientRecord?.data_x0020_de_x0020_nascimento || '';
  const clinicalGenderRaw = patientRecord?.patientGender || patientRecord?.sexo || '';
  const clinicalGender = ['M', 'Masculino'].includes(clinicalGenderRaw)
    ? 'Masculino'
    : ['F', 'Feminino'].includes(clinicalGenderRaw)
      ? 'Feminino'
      : clinicalGenderRaw || '-';
  const clinicalHeightCm = Number(patientRecord?.patientHeightCm ?? patientRecord?.altura_x0020__x0028_cm_x0029_ ?? 0);
  const clinicalWeightKg = Number(patientRecord?.patientWeightKg ?? patientRecord?.peso ?? 0);
  const clinicalConditions = Array.isArray(patientRecord?.patientMedicalInfoConditions)
    ? patientRecord.patientMedicalInfoConditions.join(', ')
    : patientRecord?.diagn_x00f3_sticos || '';
  const hasPhysicalData = clinicalHeightCm > 0 && clinicalWeightKg > 0;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <UserIcon className="w-5 h-5 text-primary-600 mr-2" />
                    <h3 className="font-display font-semibold text-lg">Informações de Login</h3>
                  </div>
                  <LinkedPatientBadge
                    isLinked={!!patientRecord}
                    size="sm"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-neutral-500">Nome</p>
                    <p className="font-medium">{userData?.userDisplayName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Email</p>
                    <p className="font-medium">{userData?.userEmail}</p>
                  </div>
              </div>
            </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-center mb-4">
                  <FileText className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="font-display font-semibold text-lg">Exames e Documentos</h3>
                </div>
                <div>
                  <p className="text-neutral-600">{records.length} itens no total</p>
                  <p className="text-neutral-600">
                    {records.filter(r => {
                      const today = new Date();
                      const recordDate = new Date(r.uploadDate);
                      return recordDate.getMonth() === today.getMonth();
                    }).length} itens este mês
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-center mb-4">
                  <Activity className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="font-display font-semibold text-lg">Avaliações Clínicas</h3>
                </div>
                <div>
                  <p className="text-neutral-600">{assessments.length} avaliações realizadas</p>
                  {assessments.length > 0 && (
                    <p className="text-sm text-neutral-500 mt-1">
                      Última: {format(
                        assessments[0]?.createdAt?.toDate?.() || new Date(assessments[0]?.datetime),
                        "dd/MM/yyyy",
                        { locale: ptBR }
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-center mb-4">
                  <ClipboardList className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="font-display font-semibold text-lg">Solicitações</h3>
                </div>
                <div>
                  <p className="text-neutral-600">
                    {evolutionRequests.filter(r => r.status === 'pending').length} solicitações pendentes
                  </p>
                  <p className="text-neutral-600">
                    {evolutionRequests.filter(r => r.status === 'approved').length} aprovadas
                  </p>
                </div>
              </div>
            </div>

            {loadingPatientRecord ? (
              <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-neutral-600">Carregando dados clínicos...</span>
                </div>
              </div>
            ) : patientRecord ? (
              <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-center mb-4">
                  <Activity className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="font-display font-semibold text-lg">Dados Clínicos</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-neutral-500">Nome Completo</p>
                      <p className="font-medium">{clinicalFullName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">CPF</p>
                      <p className="font-medium text-primary-600">{clinicalRecordNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">Data de Nascimento</p>
                      <p className="font-medium">{clinicalBirthDate || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">Idade</p>
                      <p className="font-medium">{clinicalBirthDate ? `${calculateAge(clinicalBirthDate)} anos` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">Sexo</p>
                      <p className="font-medium">{clinicalGender}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-neutral-500">Altura</p>
                      <p className="font-medium">{clinicalHeightCm.toFixed(0)} cm</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">Peso</p>
                      <p className="font-medium">{clinicalWeightKg.toFixed(1)} kg</p>
                    </div>
                    {hasPhysicalData && (
                      <>
                        <div>
                          <p className="text-sm text-neutral-500">IMC</p>
                          <p className="font-medium">
                            {calculateIMC(clinicalWeightKg, clinicalHeightCm).toFixed(1)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-neutral-500">Classificação</p>
                          <p className="font-medium">
                            {getIMCClassification(calculateIMC(clinicalWeightKg, clinicalHeightCm))}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="space-y-3">
                    {clinicalConditions && (
                      <div>
                        <p className="text-sm text-neutral-500">Diagnósticos</p>
                        <p className="font-medium text-sm">{clinicalConditions}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary-600" />
                      <span className="text-sm text-neutral-600">Seu cadastro está vinculado ao seu usuário</span>
                    </div>
                    <LinkedPatientBadge isLinked={true} size="sm" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 rounded-lg shadow-sm p-6 border border-yellow-200">
                <div className="flex items-center">
                  <Activity className="w-5 h-5 text-yellow-600 mr-2" />
                  <div>
                    <h3 className="font-display font-semibold text-lg text-yellow-900">Cadastro Não Vinculado</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Seu usuário ainda não foi vinculado a um cadastro de paciente. Entre em contato com a administração para vincular sua conta.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'records':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display font-semibold text-xl">Meus Exames e Documentos</h3>
            </div>

            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar exames e documentos..."
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRecords.map((record) => (
                  <div key={record.id} className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-neutral-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-neutral-900">
                            {record.name}
                          </div>
                          <div className="text-sm text-neutral-500">
                            Médico: {record.doctorName}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => void handleDownload(record)}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center text-sm"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Baixar
                      </button>
                    </div>
                    <div className="text-sm text-neutral-500">
                      {format(record.uploadDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </div>
                    {record.clientName && (
                      <div className="text-xs text-neutral-500 mt-1">
                        Origem: {record.clientName}
                      </div>
                    )}
                  </div>
                ))}

                {filteredRecords.length === 0 && (
                  <div className="text-center py-8 bg-neutral-50 rounded-lg">
                    <p className="text-neutral-600">Nenhum documento encontrado</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'assessments':
        if (!hasConsent) {
          return (
            <div className="bg-blue-50 rounded-lg shadow-sm p-8 border border-blue-200 text-center">
              <Activity className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-blue-900 mb-2">Consentimento Necessário</h3>
              <p className="text-blue-700 mb-6">
                Para visualizar suas avaliações clínicas, é necessário aceitar o termo de consentimento.
              </p>
              <button
                onClick={() => setShowConsentModal(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ler e Aceitar Termo
              </button>
            </div>
          );
        }

        return (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display font-semibold text-xl">Minhas Avaliações Clínicas</h3>
              <button
                onClick={toggleAssessmentSort}
                className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors text-sm"
              >
                <ArrowUpDown className="w-4 h-4" />
                {assessmentSortOrder === 'desc' ? 'Mais Recentes' : 'Mais Antigas'}
              </button>
            </div>
            {pendingConsentClientIds.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Existem consentimentos pendentes em: {pendingConsentClientIds.map(resolveClientName).join(', ')}.
                Esses tenants não entram na sua visão consolidada até o aceite.
              </div>
            )}

            {loadingAssessments ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : sortedAssessments.length === 0 ? (
              <div className="text-center py-12 bg-neutral-50 rounded-lg">
                <Activity className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                <p className="text-neutral-600 text-lg">Nenhuma avaliação clínica encontrada</p>
                <p className="text-neutral-500 text-sm mt-2">
                  Suas avaliações aparecerão aqui após serem realizadas
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sortedAssessments.map((assessment) => (
                  <div
                    key={assessment.id}
                    className="border border-neutral-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setSelectedAssessment(assessment)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary-600" />
                        <h4 className="font-semibold text-neutral-900">
                          {getTestTypeLabel(assessment.type)}
                        </h4>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Calendar className="w-4 h-4" />
                        {format(
                          assessment.createdAt?.toDate?.() || new Date(assessment.datetime),
                          "dd/MM/yyyy 'às' HH:mm",
                          { locale: ptBR }
                        )}
                      </div>
                      {assessment.doctorName && (
                        <div className="flex items-center gap-2 text-neutral-600">
                          <UserIcon className="w-4 h-4" />
                          {assessment.doctorName}
                        </div>
                      )}
                      {assessment.clientName && (
                        <div className="text-xs text-neutral-500">
                          Origem: {assessment.clientName}
                        </div>
                      )}
                      {assessment.tests && assessment.tests.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {assessment.tests.slice(0, 3).map((test, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs"
                            >
                              {getTestTypeLabel(test)}
                            </span>
                          ))}
                          {assessment.tests.length > 3 && (
                            <span className="px-2 py-1 bg-neutral-100 text-neutral-600 rounded text-xs">
                              +{assessment.tests.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-200">
                      <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                        Ver Detalhes →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'request-evolutions':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
              <div className="flex items-center gap-3 mb-6">
                <ClipboardList className="w-6 h-6 text-primary-600" />
                <h3 className="font-display font-semibold text-xl">Solicitar Evoluções Clínicas</h3>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  As evoluções clínicas contêm informações detalhadas sobre seus atendimentos.
                  Por questões de privacidade, elas não ficam automaticamente disponíveis.
                  Você pode solicitar acesso através deste formulário.
                </p>
              </div>

              <div className="space-y-4">
                {!evolutionRequestsAvailable && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Esta funcionalidade está temporariamente indisponível. Sua solicitação ainda não pode ser enviada por este canal.
                  </div>
                )}
                {!hasConsentInSelectedClient && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Para enviar solicitação neste tenant, aceite o consentimento no tenant atualmente selecionado.
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Motivo da Solicitação
                  </label>
                  <textarea
                    value={requestNotes}
                    onChange={(e) => setRequestNotes(e.target.value)}
                    placeholder="Descreva o motivo da sua solicitação (ex: necessário para consulta com outro profissional, para fins de seguro, etc.)"
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    rows={4}
                    disabled={!evolutionRequestsAvailable || !hasConsentInSelectedClient}
                  />
                </div>

                <button
                  onClick={handleRequestEvolution}
                  disabled={isSubmittingRequest || !requestNotes.trim() || !evolutionRequestsAvailable || !hasConsentInSelectedClient}
                  className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmittingRequest ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar Solicitação
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
              <h3 className="font-semibold text-lg text-neutral-900 mb-4">Minhas Solicitações</h3>

              {evolutionRequests.length === 0 ? (
                <div className="text-center py-8 bg-neutral-50 rounded-lg">
                  <ClipboardList className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
                  <p className="text-neutral-600">Nenhuma solicitação realizada ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {evolutionRequests.map((request) => (
                    <div
                      key={request.id}
                      className="border border-neutral-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {request.status === 'pending' && (
                            <>
                              <Clock className="w-5 h-5 text-amber-500" />
                              <span className="font-medium text-amber-700">Pendente</span>
                            </>
                          )}
                          {request.status === 'approved' && (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="font-medium text-green-700">Aprovada</span>
                            </>
                          )}
                          {request.status === 'rejected' && (
                            <>
                              <XCircle className="w-5 h-5 text-red-500" />
                              <span className="font-medium text-red-700">Rejeitada</span>
                            </>
                          )}
                        </div>
                        <span className="text-sm text-neutral-500">
                          {format(
                            request.requestDate?.toDate?.() || new Date(),
                            "dd/MM/yyyy 'às' HH:mm",
                            { locale: ptBR }
                          )}
                        </span>
                      </div>
                      {request.notes && (
                        <p className="text-sm text-neutral-700 mt-2">{request.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="revahub-app min-h-screen bg-neutral-100 pt-16 pb-20">
      <div className="bg-primary-600 py-10 mb-6">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-white mb-2">
              Olá, {userData?.userDisplayName}
            </h1>
            <p className="text-primary-100">
              Bem-vindo à sua área de paciente. Aqui você pode acompanhar seus exames e documentos.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-wrap border-b border-neutral-200 mb-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-neutral-600 hover:text-primary-600'
              }`}
            >
              Visão Geral
            </button>
            <button
              onClick={() => {
                if (!hasConsent) {
                  setShowConsentModal(true);
                } else {
                  setActiveTab('assessments');
                }
              }}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'assessments'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-neutral-600 hover:text-primary-600'
              }`}
            >
              Avaliações Clínicas
            </button>
            <button
              onClick={() => setActiveTab('records')}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'records'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-neutral-600 hover:text-primary-600'
              }`}
            >
              Exames e Documentos
            </button>
            <button
              onClick={() => setActiveTab('request-evolutions')}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'request-evolutions'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-neutral-600 hover:text-primary-600'
              }`}
            >
              Solicitar Evoluções
            </button>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </div>

      {/* Modals */}
      {showConsentModal && (
        <PrivacyConsentModal
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
        />
      )}

      {selectedAssessment && (
        <AssessmentDetailModal
          assessment={selectedAssessment}
          onClose={() => setSelectedAssessment(null)}
        />
      )}
    </div>
  );
};

export default PatientDashboard;
